import { useFormik } from 'formik';
import Grid from '@mui/material/Grid';
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Loading from 'components/system/Loading';
import Hint from 'components/system/Hint';
import withNotification from 'components/hoc/withNotification';
import { billingDetailsSchema } from 'schemas/billing.schema';
import colors from 'styles/colors';
import usePaymentsStore from 'store/paymentStore';
import { attachBillingDetailsFormInputs, cardBrandDetails } from 'constants/payments';
import useUpdateBillingDetails from 'hooks/useUpdateBillingDetails';
import Tooltip from '@mui/material/Tooltip';
import Input from 'components/system/Input';

const TOOLTIP_TITLE = 'These fields are not editable. Please remove and add a new card if you want to change them';
const CardInfo = ({ card }) => {
  const {
    last4, expMonth, expYear, brand,
  } = card || {};
  const { logo: CardLogo } = cardBrandDetails[brand] || {};

  const cardInfo = `  ••••  ••••  ••••  ${last4}     ${expMonth}/${expYear}`;

  return (
    <Tooltip title={TOOLTIP_TITLE}>
      <Grid
        item
        md={12}
        xs={12}
        sx={{ marginBottom: '10px' }}
      >
        <Input
          sx={{
            width: '100%',
            '& .MuiInputBase-root': {
              padding: '12px 14px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '12px',
            },
            '& .MuiInputBase-root .MuiInputBase-input.Mui-disabled': {
              '-webkit-text-fill-color': `${colors.text.disabled} !important`,
            },
          }}
          label="Card Details"
          value={cardInfo}
          disabled
          InputProps={{
            startAdornment: CardLogo && (
              <div
                style={{
                  height: '30px',
                  transform: 'scale(0.7)',
                  transformOrigin: 'center',
                  display: 'flex',
                }}
              >
                <CardLogo />
              </div>
            ),
          }}
        />
      </Grid>
    </Tooltip>
  );
};

const FormInput = ({ config, formik, onChange }) => {
  const {
    component: InputComponent, name, grid, ...props
  } = config;
  return (
    <Grid
      item
      md={grid}
      xs={12}
      sx={{ marginBottom: '10px' }}
    >
      <InputComponent
        {...props}
        sx={{ width: '100%' }}
        autoComplete="off"
        value={formik.values[name]}
        error={formik.touched[name] && formik.errors[name]}
        name={name}
        onChange={onChange}
      />
    </Grid>
  );
};

const BillingDetailsForm = ({
  hint,
  showSuccessNotification,
  showErrorNotification,
  onSuccess,
}) => {
  const { card, isLoading } = usePaymentsStore((state) => state.paymentMethod);
  const billingDetails = card?.billingDetails;
  const billingAddress = billingDetails?.address || {};
  const paymentMethodId = card?.id;

  const { mutate } = useUpdateBillingDetails(onSuccess);

  const initialFormValues = {
    name: billingDetails?.name || '',
    line1: billingAddress.line1 || '',
    line2: billingAddress.line2 || '',
    city: billingAddress.city || '',
    state: billingAddress.state || '',
    postalCode: billingAddress.postalCode || '',
    country: billingAddress.country || '',
  };

  const formik = useFormik({
    initialValues: initialFormValues,
    validationSchema: billingDetailsSchema,
    enableReinitialize: true,
    onSubmit: async (values, formikHelpers) => {
      try {
        const { name, ...rest } = values;
        const newBillingDetails = {
          paymentMethodId,
          name,
          address: { ...rest },
        };
        await mutate(newBillingDetails);
        showSuccessNotification('Billing details updated successfully.', undefined, {
          autoClose: 1500,
        });
      } catch (error) {
        showErrorNotification(
          'Something went wrong while updating your billing details. Please try again.',
          undefined,
          {
            autoClose: 2000,
          },
        );
      } finally {
        formikHelpers.setSubmitting(false);
      }
    },
  });

  return (
    <Box
      component="form"
      sx={{ width: '420px', minHeight: '456px' }}
      onSubmit={formik.handleSubmit}
    >
      {isLoading ? (
        <Loading />
      ) : (
        <>
          {hint && <Hint>{hint}</Hint>}
          <Typography variant="h5">Update Billing Details</Typography>
          <Grid container direction="row" spacing={2}>
            <Grid item xs={12} sx={{ marginTop: '16px' }}>
              <CardInfo card={card?.card} />
            </Grid>
            {attachBillingDetailsFormInputs.map((config) => (
              <FormInput
                key={config.name}
                config={config}
                formik={formik}
                onChange={formik.handleChange}
              />
            ))}
            <Grid item xs={12}>
              <LoadingButton
                fullWidth
                id="billingFormSubmit"
                variant="contained"
                type="submit"
                loadingPosition="end"
                endIcon={<span />}
                loading={formik.isSubmitting}
                disabled={formik.isSubmitting}
              >
                Submit
              </LoadingButton>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
};

export default withNotification(BillingDetailsForm);
